import { useState } from "react";
import { Search, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, usd, fmtDate, TXN_LABELS } from "@/lib/api";
import { useFetch, PageHeader, Panel, StatusBadge, Table, Empty, Loading, FilterTabs } from "@/components/common";
import { PartnerDialog } from "@/components/admin";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function UserDetail({ id, onClose, onChanged, onPromote }) {
  const { data, loading, reload } = useFetch(`/admin/users/${id}`);
  const [adj, setAdj] = useState({ amount: "", direction: "credit", note: "" });
  const [maxW, setMaxW] = useState(null);
  const [busy, setBusy] = useState(false);
  const u = data?.user;
  const patch = async (body) => {
    try {
      await api.patch(`/admin/users/${id}`, body);
      toast.success("User updated");
      reload();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const adjust = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/users/${id}/adjust`, { ...adj, amount: Number(adj.amount) });
      toast.success("Balance adjusted");
      setAdj({ amount: "", direction: "credit", note: "" });
      reload();
      onChanged();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#121722] border-[#242F42] text-white max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
        <DialogHeader><DialogTitle className="font-display">User details</DialogTitle></DialogHeader>
        {loading || !u ? <Loading /> : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-lg font-semibold">{u.name}</p><StatusBadge status={u.status} />
              {u.is_youtuber && <span className="inline-flex items-center gap-1 rounded-full border border-[#10B981]/30 bg-[#10B981]/10 px-2 py-0.5 text-xs text-[#10B981]"><Crown size={12} />YouTuber</span>}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              {[["User ID", u.user_code], ["Email", u.email], ["Mobile", u.mobile], ["Referral code", u.referral_code],
                ["Balance", usd(u.balance)], ["Locked bonus", usd(u.bonus_balance)], ["Deposits", usd(data.total_deposits)], ["Withdrawals", usd(data.total_withdrawals)],
                ["Referred by", data.referrer?.user_code || "—"], ["Referrals", data.referred_count], ["Joined", fmtDate(u.created_at)], ["First deposit", u.first_deposit_at ? fmtDate(u.first_deposit_at) : "—"]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-[#1E2738] bg-[#0B0E14] p-2.5"><p className="text-[11px] text-slate-500">{l}</p><p className="num truncate text-xs text-white">{v}</p></div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button className={u.status === "active" ? "nx-btn-danger" : "nx-btn-primary"} onClick={() => patch({ status: u.status === "active" ? "suspended" : "active" })} data-testid="toggle-user-status-btn">
                {u.status === "active" ? "Suspend account" : "Reactivate account"}
              </button>
              <button className="nx-btn-ghost" onClick={() => patch({ withdrawals_enabled: !u.withdrawals_enabled })} data-testid="toggle-user-withdrawals-btn">
                {u.withdrawals_enabled ? "Disable withdrawals" : "Enable withdrawals"}
              </button>
              {u.role !== "admin" && <button className="nx-btn-secondary" onClick={() => onPromote(u)} data-testid="promote-youtuber-btn"><Crown size={14} />{u.is_youtuber ? "Edit partner terms" : "Promote to YouTuber"}</button>}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-[#1E2738] p-4">
                <p className="nx-label">Max withdrawal limit (USD, blank = platform default)</p>
                <div className="flex gap-2">
                  <input type="number" min="0" className="nx-input num" value={maxW ?? (u.max_withdrawal || "")} onChange={(e) => setMaxW(e.target.value)} data-testid="user-max-withdrawal-input" />
                  <button className="nx-btn-secondary" onClick={() => patch({ max_withdrawal: maxW === "" || maxW === null ? null : Number(maxW) })} data-testid="save-max-withdrawal-btn">Save</button>
                </div>
              </div>
              <form onSubmit={adjust} className="space-y-2 rounded-xl border border-[#1E2738] p-4">
                <p className="nx-label">Manual balance adjustment</p>
                <div className="flex gap-2">
                  <select className="nx-input w-32" value={adj.direction} onChange={(e) => setAdj({ ...adj, direction: e.target.value })} data-testid="adjust-direction-select">
                    <option value="credit">Credit</option><option value="debit">Debit</option>
                  </select>
                  <input type="number" step="0.01" min="0.01" required placeholder="Amount" className="nx-input num" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} data-testid="adjust-amount-input" />
                </div>
                <input required minLength={3} placeholder="Reason (shown to user)" className="nx-input" value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} data-testid="adjust-note-input" />
                <button className="nx-btn-primary w-full" disabled={busy} data-testid="adjust-submit-btn">{busy && <Loader2 size={14} className="animate-spin" />}Apply</button>
              </form>
            </div>
            <div className="rounded-xl border border-[#1E2738]">
              <Table head={["Date", "Type", "Description", "Amount", "Status"]}>
                {!data.transactions.length && <Empty cols={5} text="No transactions" />}
                {data.transactions.slice(0, 30).map((t) => (
                  <tr key={t.id}><td className="nx-td text-xs">{fmtDate(t.created_at)}</td><td className="nx-td text-xs">{TXN_LABELS[t.type] || t.type}</td>
                    <td className="nx-td text-xs">{t.description}</td><td className="nx-td num text-xs">{t.direction === "debit" ? "−" : "+"}{usd(t.amount)}</td><td className="nx-td"><StatusBadge status={t.status} /></td></tr>
                ))}
              </Table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const { data, loading, reload } = useFetch(`/admin/users?search=${encodeURIComponent(search)}&role=${role}`);
  const [sel, setSel] = useState(null);
  const [promote, setPromote] = useState(null);
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Admin" title="Users" subtitle="Manage accounts, balances, limits and partner status." />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <form onSubmit={(e) => { e.preventDefault(); setSearch(q); }} className="flex w-full max-w-md gap-2">
          <div className="relative flex-1"><Search size={15} className="absolute left-3 top-3 text-slate-500" />
            <input className="nx-input pl-9" placeholder="Search name, email, user ID, mobile, code" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-user-search-input" /></div>
          <button className="nx-btn-secondary" data-testid="admin-user-search-btn">Search</button>
        </form>
        <FilterTabs value={role} onChange={setRole} options={[["", "All"], ["user", "Users"], ["youtuber", "YouTubers"], ["admin", "Admins"]]} testid="user-role-filter" />
      </div>
      <Panel>
        {loading ? <Loading /> : (
          <Table head={["User ID", "Name", "Email", "Balance", "Bonus", "Role", "Status", "Joined"]} testid="admin-users-table">
            {!data?.length && <Empty cols={8} text="No users found" />}
            {data?.map((u) => (
              <tr key={u.id} onClick={() => setSel(u.id)} className="cursor-pointer hover:bg-[#1A2232]" data-testid={`user-row-${u.user_code}`}>
                <td className="nx-td num text-[#10B981]">{u.user_code}</td>
                <td className="nx-td text-white">{u.name}</td>
                <td className="nx-td">{u.email}</td>
                <td className="nx-td num">{usd(u.balance)}</td>
                <td className="nx-td num">{usd(u.bonus_balance)}</td>
                <td className="nx-td">{u.role === "admin" ? "Admin" : u.is_youtuber ? <span className="inline-flex items-center gap-1 text-[#10B981]"><Crown size={12} />YouTuber</span> : "User"}</td>
                <td className="nx-td"><StatusBadge status={u.status} /></td>
                <td className="nx-td text-xs">{fmtDate(u.created_at)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      {sel && <UserDetail id={sel} onClose={() => setSel(null)} onChanged={reload} onPromote={(u) => { setSel(null); setPromote(u); }} />}
      <PartnerDialog target={promote} onClose={() => setPromote(null)} onDone={() => { setPromote(null); reload(); }} />
    </div>
  );
}
